import { Button } from '@avanta/shared/components/ui/Button';
import { Calendar } from '@avanta/shared/components/ui/Calendar';
import { Select } from '@avanta/shared/components/ui/Select';
import { Separator } from '@avanta/shared/components/ui/Separator';
import { APPOINTMENTS_SCHEDULE_DAYS, LOCIZE_NS } from '@avanta/shared/constants/common';
import { useHasVerticalOverflow } from '@avanta/shared/hooks/useHasVerticalOverflow';
import { checkIsDayDisabled, getFirstAvailableDate } from '@avanta/shared/utils/appointments';
import { cn } from '@avanta/shared/utils/cn';
import { languageOptions } from '@avanta/shared/utils/common';
import { addDays, isSameMonth, startOfDay } from 'date-fns';
import { ChevronRight, Languages } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AnimatedContainer } from 'components/ui/AnimatedContainer';
import { MOBILE_MAX_WIDTH } from 'constants/common';
import { useWindowWidth } from 'hooks/useWindowWidth';
import { CONTAINER_MD_MAX_WIDTH } from '../../../styles';
import { AppointmentsReservePayload, type StepProps } from '../../../types';
import { BackButton } from '../../ui/BackButton';
import { StepSection } from '../../ui/StepSection';
import { DateTimeStepSkeleton } from './components/DateTimeStepSkeleton';
import { SlotsGrid } from './components/SlotsGrid';
import { useFetchFreeHours } from './useFetchFreeHours';
import { getAllTimeSlotsForDate } from './utils';

export const DateTimeStep = ({ handleNext, handleBack }: StepProps): React.ReactElement => {
  const { setValue, watch } = useFormContext();
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const { availableDates, isLoading, isFetching } = useFetchFreeHours();
  const [dateTime, timezone, language] = watch([
    AppointmentsReservePayload.dateTime,
    AppointmentsReservePayload.timezone,
    AppointmentsReservePayload.language,
  ]);

  const showSkeleton = isLoading || isFetching;

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    getFirstAvailableDate(availableDates)
  );
  const [prevAvailableDates, setPrevAvailableDates] = useState(availableDates);
  const today = useMemo(() => startOfDay(new Date()), []);
  const endDate = useMemo(() => startOfDay(addDays(today, APPOINTMENTS_SCHEDULE_DAYS)), [today]);

  if (prevAvailableDates !== availableDates) {
    setPrevAvailableDates(availableDates);
    if (!selectedDate) {
      setSelectedDate(getFirstAvailableDate(availableDates));
    }
  }

  const { timeSlots, emotionalSupportSlots, allSlots } = useMemo(() => {
    if (!selectedDate || !timezone)
      return { timeSlots: [], emotionalSupportSlots: [], allSlots: [] };
    return getAllTimeSlotsForDate(selectedDate, availableDates);
  }, [selectedDate, availableDates, timezone]);

  const layoutRootRef = useRef<HTMLDivElement>(null);
  const { hasOverflow, isAtBottom } = useHasVerticalOverflow(
    layoutRootRef,
    'nearestScrollParent',
    allSlots
  );

  const listHasScroll = hasOverflow && !isAtBottom;
  const width = useWindowWidth().width;
  const isMobile = width < MOBILE_MAX_WIDTH;

  const isStickyFooter = !!dateTime && isMobile;
  const isScrollingFooter = isStickyFooter && !isAtBottom;

  const isDayDisabled = useCallback(
    (day: Date): boolean => checkIsDayDisabled(day, availableDates, today, endDate),
    [availableDates, today, endDate]
  );

  const handleOnSelectDay = (date: Date): void => {
    setSelectedDate(date);
    setValue(AppointmentsReservePayload.dateTime, '');
  };

  return (
    <AnimatedContainer
      ref={layoutRootRef}
      className={cn(CONTAINER_MD_MAX_WIDTH, 'flex flex-col gap-8 am-lg:gap-10')}
    >
      <StepSection
        title={t('AppointmentsReserve.Steps.dateTime.section.timeZone&Lang')}
        className="grid am-lg:grid-cols-2 gap-2 am-lg:gap-4"
        containerClassName="gap-4"
      >
        <Select
          options={languageOptions}
          placeholder={t(
            'AppointmentsReserve.Steps.dateTime.section.timeZone&Lang.lang.placeholder'
          )}
          label={t('AppointmentsReserve.Steps.dateTime.section.timeZone&Lang.lang.label')}
          className="w-full"
          value={language}
          onValueChange={value => {
            setValue(AppointmentsReservePayload.dateTime, '');
            setValue(AppointmentsReservePayload.language, value);
          }}
          leftIcon={Languages}
        />
      </StepSection>
      {timezone && language && (
        <>
          {showSkeleton ? (
            <DateTimeStepSkeleton />
          ) : (
            <div className="flex flex-col gap-8 am-lg:gap-10 transition-opacity">
              <StepSection
                title={t('AppointmentsReserve.Steps.dateTime.section.schedule.title')}
                className="grid am-lg:grid-cols-2 gap-4 am-lg:gap-12 p-0 am-lg:p-6 border border-transparent am-lg:border-teal-300 rounded-xl"
              >
                <Calendar
                  className="w-full border border-teal-300 am-lg:border-transparent rounded-xl px-2 am-lg:p-0"
                  selected={selectedDate}
                  startMonth={today}
                  endMonth={endDate}
                  onSelect={handleOnSelectDay}
                  disabled={isDayDisabled}
                  showOutsideDays
                  {...(isSameMonth(today, endDate) && { hideNavigation: true })}
                />
                <div className="flex flex-col gap-5">
                  <SlotsGrid
                    slots={timeSlots}
                    title={t('AppointmentsReserve.Steps.dateTime.section.schedule.time.title')}
                    selectedDate={selectedDate}
                    mode="primary"
                  />
                  <Separator />
                  <SlotsGrid
                    slots={emotionalSupportSlots}
                    title={t('AppointmentsReserve.Steps.dateTime.section.schedule.time.title')}
                    subtitle={t(
                      'AppointmentsReserve.Steps.dateTime.section.schedule.timeSlots.other.subtitle'
                    )}
                    selectedDate={selectedDate}
                    tooltip={t(
                      'AppointmentsReserve.Steps.dateTime.section.schedule.timeSlots.other.tooltip'
                    )}
                    mode="secondary"
                  />
                </div>
              </StepSection>
            </div>
          )}
          <div
            className={cn(
              'flex justify-between gap-4',
              isStickyFooter && 'sticky bottom-0 left-0 right-0 bg-white',
              isScrollingFooter && 'h-14 pt-3.5 pb-2',
              listHasScroll && !!dateTime && 'shadow-mobile-menu-top -mx-4 justify-center'
            )}
          >
            {!isScrollingFooter && <BackButton handleBack={handleBack} />}
            <Button
              type="button"
              variant="primary"
              className={cn(
                'flex-1 am-lg:w-fit am-lg:flex-none',
                listHasScroll && !!dateTime && 'w-9/10 flex-none'
              )}
              size={isScrollingFooter ? 'md' : 'lg'}
              icon="trailing"
              onClick={handleNext}
              disabled={!dateTime}
            >
              {t('AppointmentsReserve.next')}
              <ChevronRight className="size-5" />
            </Button>
          </div>
        </>
      )}
    </AnimatedContainer>
  );
};
